<template>
  <a-input
    v-model:value="displayValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :maxlength="maxDigits"
    :status="computedStatus"
    class="ant-telephone-input"
    @update:value="onInput"
    @blur="onBlur"
    @pressEnter="onPressEnter"
  >
    <template #prefix>
      <i class="fas fa-phone" />
    </template>
  </a-input>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

interface Props {
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  status?: "" | "error" | "warning";
  /** Chỉ giữ số khi emit (mặc định true) */
  digitsOnly?: boolean;
  /** Độ dài tối đa số điện thoại VN */
  maxDigits?: number;
  validateOnBlur?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  placeholder: "Nhập số điện thoại",
  disabled: false,
  size: "middle",
  allowClear: true,
  status: "",
  digitsOnly: true,
  maxDigits: 11,
  validateOnBlur: true,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "blur", event: FocusEvent): void;
  (e: "pressEnter", event: KeyboardEvent): void;
  (e: "change", value: string): void;
  (e: "validate", valid: boolean): void;
}>();

const displayValue = ref("");

const onlyDigits = (value: string) => value.replace(/\D/g, "").slice(0, props.maxDigits);

const formatPhone = (digits: string) => {
  if (!digits) return "";
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  if (digits.length <= 10) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
};

const isValidPhone = (digits: string) => {
  if (!digits) return true;
  return /^(0[3|5|7|8|9])[0-9]{8}$|^01[2689][0-9]{8}$/.test(digits);
};

watch(
  () => props.modelValue,
  (val) => {
    const digits = onlyDigits(val ?? "");
    displayValue.value = formatPhone(digits);
  },
  { immediate: true }
);

const localError = computed(() => {
  if (!props.validateOnBlur) return false;
  const digits = onlyDigits(props.modelValue ?? "");
  return !!digits && !isValidPhone(digits);
});

const computedStatus = computed(() => {
  if (props.status) return props.status;
  return localError.value ? "error" : "";
});

const onInput = (val: string) => {
  const digits = onlyDigits(val);
  displayValue.value = formatPhone(digits);
  const emitValue = props.digitsOnly ? digits : displayValue.value;
  emit("update:modelValue", emitValue);
  emit("change", emitValue);
};

const onBlur = (e: FocusEvent) => {
  const digits = onlyDigits(props.modelValue ?? "");
  emit("validate", isValidPhone(digits));
  emit("blur", e);
};

const onPressEnter = (e: KeyboardEvent) => emit("pressEnter", e);
</script>

<!--
  Hướng dẫn sử dụng — Telephone
  Global: <h-telephone v-model="phone" />
  Import: import { TelephoneInput } from '@/components/ant/input'

  Props: modelValue, placeholder, disabled, size, allowClear, status, digitsOnly, maxDigits, validateOnBlur
  Events: update:modelValue, change, blur, pressEnter, validate
  Note: v-model mặc định là số thuần (0901234567)
  Docs: src/components/ant/input/README.md
-->
